import React from 'react';
import QRCode from 'qrcode.react';

const IdCardV2 = ({ participant, event, elementStyles, globalVisibility, index }) => {
    // Get background image: category (free events) → ticket (paid events) → event default
    const getBackground = () => {
        if (participant.categoryId && event.participantCategories) {
            const category = event.participantCategories.find(c => c.id === participant.categoryId);
            if (category?.idCardBackground) return category.idCardBackground;
        }
        if (participant.ticketId && event.tickets) {
            const ticket = event.tickets.find(t => t.id === participant.ticketId);
            if (ticket?.idCardBackground) return ticket.idCardBackground;
        }
        return event.defaultIdCardBackground || null;
    };

    // Format ticket dates
    const formatTicketDates = (dates) => {
        if (!dates || dates.length === 0) return "";
        return dates
            .map((date) =>
                new Date(date).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                })
            )
            .join(", ");
    };

    // Get ticket category name
    const getTicketCategory = () => {
        if (participant.ticketId && event.tickets) {
            const ticket = event.tickets.find(t => t.id === participant.ticketId);
            return ticket?.name || '';
        }
        return '';
    };

    // Get ticket dates
    const getTicketDates = () => {
        if (participant.ticketId && event.tickets) {
            const ticket = event.tickets.find(t => t.id === participant.ticketId);
            if (ticket?.priceByDate && ticket.priceByDate.length > 0) {
                return ticket.priceByDate.map(pd => pd.date);
            }
        }
        // Fallback to event date
        return event.eventDate ? [event.eventDate] : [];
    };

    const background = getBackground();

    return (
        <div
            className="relative rounded-[1px] h-[610px] w-[430px] shadow-lg"
            style={{
                backgroundImage: background ? `url(${background})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            <div className="relative z-10 h-full text-white">
                {background && (
                    <div className="absolute inset-0">
                        <img
                            src={background}
                            alt=""
                            className="w-full h-full object-cover rounded-[1px]"
                        />
                    </div>
                )}

                <div className="relative h-full flex flex-col justify-between p-4 items-center">
                    {/* Render dynamic participant fields */}
                    {event.participantFields?.map((field, idx) => {
                        const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
                        const style = elementStyles?.[fieldKey];
                        const visible = globalVisibility?.[fieldKey];
                        // data is stored by fieldName; fall back to field.id for legacy records
                        const value = participant.data?.[field.fieldName] ?? participant.data?.[field.id];

                        if (!visible || !value || !style) return null;

                        // Special handling for profile picture
                        if (field.fieldType === 'file' && field.fieldName.toLowerCase().includes('picture')) {
                            return (
                                <div
                                    key={fieldKey}
                                    style={{
                                        position: 'absolute',
                                        top: style.top ? `${style.top}px` : undefined,
                                        bottom: style.bottom ? `${style.bottom}px` : undefined,
                                        left: typeof style.left === 'number' ? `${style.left}px` : (style.left || '50%'),
                                        right: style.right,
                                        transform: (style.left === '50%' || style.left === 215) ? 'translateX(-50%)' :
                                            style.left === '100%' ? 'translateX(-100%)' : undefined
                                    }}
                                >
                                    <img
                                        src={value}
                                        alt={field.fieldName}
                                        style={{
                                            width: `${style.size || 170}px`,
                                            height: `${style.size || 170}px`,
                                            objectFit: 'cover',
                                            borderRadius: '50%',
                                            border: '4px solid white',
                                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                </div>
                            );
                        }

                        // Text fields
                        return (
                            <p
                                key={fieldKey}
                                style={{
                                    position: 'absolute',
                                    top: style.top ? `${style.top}px` : undefined,
                                    bottom: style.bottom ? `${style.bottom}px` : undefined,
                                    left: typeof style.left === 'number' ? `${style.left}px` : (style.left || '50%'),
                                    right: style.right,
                                    fontSize: `${style.fontSize || 16}px`,
                                    color: style.color || 'black',
                                    textAlign: style.textAlign || 'center',
                                    transform: (style.left === '50%' || style.left === 215) ? 'translateX(-50%)' :
                                        style.left === '100%' ? 'translateX(-100%)' : undefined,
                                    fontWeight: field.fieldName.toLowerCase().includes('name') ? 'bold' : 'normal',
                                    textTransform: field.fieldName.toLowerCase().includes('institute') ||
                                        field.fieldName.toLowerCase().includes('organization') ? 'uppercase' : 'none',
                                    maxWidth: '90%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {value}
                            </p>
                        );
                    })}

                    {/* QR Code - Always available */}
                    {globalVisibility?.qrCode && elementStyles?.qrCode && (
                        <div
                            style={{
                                position: 'absolute',
                                top: elementStyles.qrCode.top ? `${elementStyles.qrCode.top}px` : undefined,
                                bottom: elementStyles.qrCode.bottom ? `${elementStyles.qrCode.bottom}px` : undefined,
                                left: elementStyles.qrCode.left || '50%',
                                transform: (elementStyles.qrCode.left === '50%' || elementStyles.qrCode.left === 215) ? 'translateX(-50%)' :
                                    elementStyles.qrCode.left === '100%' ? 'translateX(-100%)' : undefined
                            }}
                            className="rounded-xl bg-white px-2 pb-3 shadow-md"
                        >
                            <div style={{ maxWidth: elementStyles.qrCode.size }} className="mx-auto">
                                <p className="text-center text-[13px] text-black font-bold py-1 break-words">
                                    Show at the Venue
                                </p>
                            </div>
                            <QRCode
                                value={`${window.location.origin}/v2/scan/${participant._id}`}
                                size={elementStyles.qrCode.size || 100}
                                level="H"
                                className="rounded-lg mx-auto"
                            />
                        </div>
                    )}

                    {/* Registration ID */}
                    {globalVisibility?.participantId && elementStyles?.participantId && participant.registrationId && (
                        <p
                            style={{
                                position: 'absolute',
                                top: elementStyles.participantId.top ? `${elementStyles.participantId.top}px` : undefined,
                                bottom: elementStyles.participantId.bottom ? `${elementStyles.participantId.bottom}px` : undefined,
                                left: elementStyles.participantId.left || '50%',
                                fontSize: `${elementStyles.participantId.fontSize || 12}px`,
                                color: elementStyles.participantId.color || 'black',
                                textAlign: elementStyles.participantId.textAlign || 'center',
                                transform: (elementStyles.participantId.left === '50%' || elementStyles.participantId.left === 215) ? 'translateX(-50%)' :
                                    elementStyles.participantId.left === '100%' ? 'translateX(-100%)' : undefined,
                                fontWeight: 'semibold'
                            }}
                        >
                            ID: {participant.registrationId}
                        </p>
                    )}

                    {/* Participant Category - For free events */}
                    {event.eventType === 'free' && globalVisibility?.categoryName && elementStyles?.categoryName && (() => {
                        const cat = participant.categoryId && event.participantCategories?.find(c => c.id === participant.categoryId);
                        const catName = cat?.name;
                        if (!catName) return null;
                        const st = elementStyles.categoryName;
                        const hex = st.bgColor || '#ffffff';
                        const opacity = st.bgOpacity ?? 0.9;
                        const r = parseInt(hex.slice(1,3),16);
                        const g = parseInt(hex.slice(3,5),16);
                        const b = parseInt(hex.slice(5,7),16);
                        return (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: st.top ? `${st.top}px` : undefined,
                                    bottom: st.bottom ? `${st.bottom}px` : undefined,
                                    left: st.left || '50%',
                                    transform: (st.left === '50%' || st.left === 215) ? 'translateX(-50%)' :
                                        st.left === '100%' ? 'translateX(-100%)' : undefined,
                                    fontSize: `${st.fontSize || 16}px`,
                                    color: st.color || 'black',
                                    textAlign: st.textAlign || 'center',
                                    fontWeight: 'bold',
                                    lineHeight: '1',
                                    padding: '6px',
                                    borderRadius: '8px',
                                    whiteSpace: 'nowrap',
                                    backgroundColor: `rgba(${r},${g},${b},${opacity})`,
                                }}
                            >
                                {catName}
                            </div>
                        );
                    })()}

                    {/* Ticket Category - For paid events */}
                    {/* {event.eventType === 'paid' && globalVisibility?.ticketCategory && elementStyles?.ticketCategory && getTicketCategory() && (
                        <div
                            style={{
                                position: 'absolute',
                                top: elementStyles.ticketCategory.top ? `${elementStyles.ticketCategory.top}px` : undefined,
                                bottom: elementStyles.ticketCategory.bottom ? `${elementStyles.ticketCategory.bottom}px` : undefined,
                                left: elementStyles.ticketCategory.left || '50%',
                                transform: elementStyles.ticketCategory.left === '50%' ? 'translateX(-50%)' : undefined,
                                fontSize: `${elementStyles.ticketCategory.fontSize || 16}px`,
                                color: elementStyles.ticketCategory.color || 'black',
                                textAlign: elementStyles.ticketCategory.textAlign || 'center',
                                fontWeight: 'bold',
                                padding: '4px 16px',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)'
                            }}
                        >
                            {getTicketCategory()}
                        </div>
                    )} */}

                    {/* Ticket Dates - For paid events */}
                    {/* {event.eventType === 'paid' && globalVisibility?.ticketDates && elementStyles?.ticketDates && getTicketDates().length > 0 && (
                        <p
                            style={{
                                position: 'absolute',
                                top: elementStyles.ticketDates.top ? `${elementStyles.ticketDates.top}px` : undefined,
                                bottom: elementStyles.ticketDates.bottom ? `${elementStyles.ticketDates.bottom}px` : undefined,
                                left: elementStyles.ticketDates.left || '50%',
                                fontSize: `${elementStyles.ticketDates.fontSize || 14}px`,
                                color: elementStyles.ticketDates.color || 'black',
                                textAlign: elementStyles.ticketDates.textAlign || 'center',
                                transform: elementStyles.ticketDates.left === '50%' ? 'translateX(-50%)' : undefined,
                                fontWeight: 'normal'
                            }}
                        >
                            {formatTicketDates(getTicketDates())}
                        </p>
                    )} */}
                </div>
            </div>
        </div>
    );
};

export default IdCardV2;
